import { useEffect, useMemo, useRef, useState } from "react";
import ReportTable from "../ReportTable.component";
import CustomReportService from "../../../services/reports/CustomReport.service";
import EpochMonthService from "../../../services/EpochMonth.service";
import ReportService from "../../../services/reports/Report.service";
import { BaseReportParsedData, MoneyDataItem, PercentDataItem } from "../../../interfaces/reports/BaseReport.interface";
import * as XLSX from "xlsx";
import MultiSelect from "../../../tailwindComponents/ui/multi-select";

interface WholeEarthCustomReportProps {
  clientId: string | number;
  epochMonth: number | null;
}

// Raw record based on GetWholeEarthCustomReportData.sql
interface WholeEarthRaw {
  locationCode: string;
  locationName: string;
  classCode: string;
  className: string;
  category: string | null;
  categorySortOrder: number | null;
  categoryId: number | null;
  epochMonthRM: number;
  fiscalStartEpoch: number;
  // requested fields
  salesLmLy: number | null; // Sales last month last year
  salesPlanLM: number | null;
  salesLM: number | null;
  salesThisYear: number | null; // FYTD this year
  salesLastYear: number | null; // Last year same month
  fytdLastYear: number | null;       // FYTD LY
  fytdThisYear: number | null; // FYTD TY (alias of salesThisYear)
  salesLast12: number | null;
  salesPlanCurrentMonth: number | null;
  salesAnnualPlan: number | null;
  plannedInventoryNextMonthBom: number | null;
  currentInventory: number | null;
  avgMonthlySalesPlan: number | null;
  avgMonthlyInventoryBom: number | null;
  inventoryBomPriorYear: number | null;
}

// Parsed record for display/grouping
interface WholeEarthParsed extends BaseReportParsedData {
  salesLmLy: MoneyDataItem;
  salesPlanLM: MoneyDataItem;
  salesLM: MoneyDataItem;
  actualTyVsLy: MoneyDataItem; // kept for compatibility (value not used directly for display)
  fytdLy: MoneyDataItem;
  fytdTy: MoneyDataItem;
  fytdLyVsLy: MoneyDataItem; // kept for compatibility (value not used directly for display)
  salesLmToTotal: PercentDataItem;
  salesL12: MoneyDataItem;
  salesL12ToTotal: PercentDataItem;
  salesPlan: MoneyDataItem; // current month plan
  salesAnnualPlan: MoneyDataItem;
  plannedInventory: MoneyDataItem; // next month BOM plan
  actualInventoryTy: MoneyDataItem; // current inventory
  actualInventoryLy: MoneyDataItem; // last year inventory BOM
  actualInventoryVsLy: PercentDataItem; // TY vs LY percent
  actualInventoryToTotal: PercentDataItem;
  avgMonthlyInventoryBom: MoneyDataItem; // for planned turn
  avgMonthlyInventoryBomPlan: MoneyDataItem; // for planned turn
  inventoryBomPriorYear: MoneyDataItem;
}

function num(v: any): number { return (v === undefined || v === null || isNaN(Number(v))) ? 0 : Number(v); }

function parseRaw(rows: WholeEarthRaw[]): WholeEarthParsed[] {
  return (rows || []).map(r => {
    const salesLmLy = num((r as any).salesLmLy);
    const salesPlanLM = num((r as any).salesPlanLM);
    const salesLM = num((r as any).salesLM);
    const fytdLy = num((r as any).fytdLastYear);
    const fytdTy = num((r as any).fytdThisYear ?? (r as any).salesThisYear);
    const salesL12 = num((r as any).salesLast12);
    const salesPlan = num((r as any).salesPlanCurrentMonth);
    const salesAnnualPlan = num((r as any).salesAnnualPlan);
    const plannedInv = num((r as any).plannedInventoryNextMonthBom);
    const actualInvTy = num((r as any).currentInventory);
    const actualInvLy = num((r as any).inventoryBomPriorYear);
	const avgMonthlyInventoryBomPlan = num((r as any).avgMonthlyInventoryBomPlan);
    const avgMonthlyInventoryBom = num((r as any).avgMonthlyInventoryBom);

    return {
      locationCode: (r as any).locationCode,
      locationName: (r as any).locationName,
      classCode: (r as any).classCode,
      className: (r as any).className,
      category: (r as any).category ?? "N/A",
      categorySortOrder: (r as any).categorySortOrder ?? 9999,
      parentClientClassCode: null,

      salesLmLy: new MoneyDataItem(salesLmLy),
      salesPlanLM: new MoneyDataItem(salesPlanLM),
      salesLM: new MoneyDataItem(salesLM),
      actualTyVsLy: new MoneyDataItem(salesLM - salesLmLy),
      fytdLy: new MoneyDataItem(fytdLy),
      fytdTy: new MoneyDataItem(fytdTy),
      fytdLyVsLy: new MoneyDataItem(fytdTy - fytdLy),
      salesLmToTotal: new PercentDataItem(0), // placeholder, computed later
      salesL12: new MoneyDataItem(salesL12),
      salesL12ToTotal: new PercentDataItem(0), // placeholder, computed later
      salesPlan: new MoneyDataItem(salesPlan),
      salesAnnualPlan: new MoneyDataItem(salesAnnualPlan),
      plannedInventory: new MoneyDataItem(plannedInv),
      actualInventoryTy: new MoneyDataItem(actualInvTy),
      actualInventoryLy: new MoneyDataItem(actualInvLy),
      actualInventoryVsLy: new PercentDataItem(0), // computed later
      actualInventoryToTotal: new PercentDataItem(0),
      avgMonthlyInventoryBom: new MoneyDataItem(avgMonthlyInventoryBom),
	  avgMonthlyInventoryBomPlan: new MoneyDataItem(avgMonthlyInventoryBomPlan),
	  inventoryBomPriorYear: new MoneyDataItem(num((r as any).inventoryBomPriorYear)),
    } as WholeEarthParsed;
  });
}

const HIERARCHY = ["category", "locationCode"] as const; // Default: Category by Location

const WholeEarthCustomReport = ({ clientId, epochMonth }: WholeEarthCustomReportProps) => {
  const raw = useRef<WholeEarthRaw[]>([]);
  const [parsed, setParsed] = useState<WholeEarthParsed[]>([]); // store ungrouped parsed rows
  const [loading, setLoading] = useState<boolean>(true);

  // filters
  const [locationFilter, setLocationFilter] = useState<string[]>(["ALL"]);
  const [categoryFilter, setCategoryFilter] = useState<string[]>(["ALL"]);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const data = await CustomReportService.getWholeEarthCustomReportData({ clientId, epochMonth: epochMonth ?? EpochMonthService.getTodayEpochMonth() });
        raw.current = (data as WholeEarthRaw[]) || [];
        const _parsed = parseRaw(raw.current);
        // store ungrouped parsed; grouping will be applied after filters
        setParsed(_parsed);
      } catch (e) {
        setParsed([]);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [clientId, epochMonth]);

  // Columns per spec (adjusted to available SQL fields)
  const columns = [
    "Category",
    "Location",
    "Sales LM LY",
    "Sales Plan LM",
    "Sales LM",
    "Actual TY vs LY",
    "FYTD LY",
    "FYTD TY",
    "FYTD TY vs LY",
    "Sales LM to Total",
    "Sales L12",
    "Sales L12 to Total",
    "Sales Plan",
    "Sales Annual Plan",
    "Turn",
    "Turn Plan",
    "Planned Inventory",
    "Actual Inventory TY",
	"Actual Inventory LY",
	"Actual Inventory TY VS LY",
    "Actual Inventory to Total",
  ];

  // Options for filters
  const locations = useMemo(() => {
    const set = new Map<string, string>();
    parsed.forEach(p => {
      if (p.locationCode) set.set(p.locationCode, `${p.locationCode} ${p.locationName}`);
    });
    return [{ code: "ALL", label: "All Locations" }, ...Array.from(set.entries()).map(([code, label]) => ({ code, label }))];
  }, [parsed]);

  const categories = useMemo(() => {
    const set = new Set<string>();
    parsed.forEach(p => {
      if (p.category) set.add(String(p.category));
    });
    const arr = Array.from(set.values()).sort((a, b) => a.localeCompare(b));
    return [{ code: "ALL", label: "All Categories" }, ...arr.map(code => ({ code, label: code }))];
  }, [parsed]);

  // Apply filters before grouping, then group to include subtotal and grand total rows
  const filteredAndGrouped = useMemo(() => {
    const locSet = new Set((Array.isArray(locationFilter) && locationFilter.includes("ALL")) ? [] : (Array.isArray(locationFilter) ? locationFilter : []));
    const catSet = new Set((Array.isArray(categoryFilter) && categoryFilter.includes("ALL")) ? [] : (Array.isArray(categoryFilter) ? categoryFilter : []));
    const base = Array.isArray(parsed) ? parsed : [];
    const filtered = base.filter(p => {
      const locOk = locSet.size === 0 || locSet.has(String(p.locationCode || ""));
      const catOk = catSet.size === 0 || catSet.has(String(p.category || ""));
      return locOk && catOk;
    });
    return ReportService.groupByHierarchy(filtered, [...HIERARCHY]) as WholeEarthParsed[];
  }, [parsed, locationFilter, categoryFilter]);

  // Compute percent-to-total columns and derived metrics
  const tableData = useMemo(() => {
    // Carrying totals for hierarchy Category -> Location based on filtered/grouped items
    let grandTotals = { salesLM: 0, salesL12: 0, inventoryTy: 0, avgMonthlyInventoryBom: 0 };

    // If a visible grand total row exists in filteredAndGrouped, use it; otherwise compute totals from items
    const hasGrandTotal = filteredAndGrouped.some((item) => {
      const labels = ReportService.hierarchyItemToVerticalDisplayNames(item, [...HIERARCHY]);
      const isCategoryTotal = String(labels[1] || "").toLowerCase() === "total";
      const isGrand = String(labels[0] || "").toLowerCase() === "total" && isCategoryTotal;
      return isGrand;
    });

    if (!hasGrandTotal) {
      filteredAndGrouped.forEach((item) => {
        grandTotals.salesLM += num(item.salesLM?.value);
        grandTotals.salesL12 += num(item.salesL12?.value);
        grandTotals.inventoryTy += num(item.actualInventoryTy?.value);
        grandTotals.avgMonthlyInventoryBom += num(item.avgMonthlyInventoryBom?.value);
      });
    }

    return filteredAndGrouped.map((item) => {
      const labels = ReportService.hierarchyItemToVerticalDisplayNames(item, [...HIERARCHY]);
      const isCategoryTotal = String(labels[1] || "").toLowerCase() === "total";
      const isGrandTotal = String(labels[0] || "").toLowerCase() === "total" && isCategoryTotal;

      const salesLMVal = num(item.salesLM?.value);
      const salesL12Val = num(item.salesL12?.value);
      const inventoryTyVal = num(item.actualInventoryTy?.value);
      const inventoryLyVal = num(item.actualInventoryLy?.value);
      const avgMonthlyInventoryBomVal = num(item.avgMonthlyInventoryBom?.value);
      const avgMonthlyInventoryBomPlanVal = num(item.avgMonthlyInventoryBomPlan?.value);

      if (isGrandTotal) {
        grandTotals = {
          salesLM: salesLMVal,
          salesL12: salesL12Val,
          inventoryTy: inventoryTyVal,
          avgMonthlyInventoryBom: avgMonthlyInventoryBomVal,
        };
      }

      const salesLmToTotal = grandTotals.salesLM ? (salesLMVal / grandTotals.salesLM) * 100 : 0;
      const salesL12ToTotal = grandTotals.salesL12 ? (salesL12Val / grandTotals.salesL12) * 100 : 0;
      const inventoryToTotal = grandTotals.inventoryTy ? (inventoryTyVal / grandTotals.inventoryTy) * 100 : 0;

      const salesLmLyVal = num(item.salesLmLy?.value);
      const tyVsLyPct = salesLmLyVal ? ((salesLMVal / salesLmLyVal) - 1) * 100 : 0;

      const fytdTyVal = num(item.fytdTy?.value);
      const fytdLyVal = num(item.fytdLy?.value);
      const fytdVsPct = fytdLyVal ? ((fytdTyVal / fytdLyVal) - 1) * 100 : 0;

      const invVsLyPct = inventoryLyVal ? ((inventoryTyVal / inventoryLyVal) - 1) * 100 : 0;

      const actualAvgInv = avgMonthlyInventoryBomVal;
      const turnDisplay = ReportService.calculateTurn({ sales: salesL12Val, invAvg: actualAvgInv, format: true }) as string;

      const planAvgInv = avgMonthlyInventoryBomPlanVal;
      const turnPlanDisplay = ReportService.calculateTurn({ sales: Number(item.salesAnnualPlan?.value) ?? 0, invAvg: planAvgInv, format: true }) as string;

      return [
        ...labels,
        ReportService.formatCurrency(num(item.salesLmLy?.value)),
        ReportService.formatCurrency(num(item.salesPlanLM?.value)),
        ReportService.formatCurrency(salesLMVal),
        ReportService.formatPercent(tyVsLyPct),
        ReportService.formatCurrency(fytdLyVal),
        ReportService.formatCurrency(fytdTyVal),
        ReportService.formatPercent(fytdVsPct),
        ReportService.formatPercent(salesLmToTotal),
        ReportService.formatCurrency(salesL12Val),
        ReportService.formatPercent(salesL12ToTotal),
        ReportService.formatCurrency(num(item.salesPlan?.value)),
        ReportService.formatCurrency(num(item.salesAnnualPlan?.value)),
        turnDisplay,
        turnPlanDisplay,
        ReportService.formatCurrency(num(item.plannedInventory?.value)),
        ReportService.formatCurrency(inventoryTyVal),
        ReportService.formatCurrency(inventoryLyVal),
        ReportService.formatPercent(invVsLyPct),
        ReportService.formatPercent(inventoryToTotal),
      ];
    });
  }, [filteredAndGrouped]);

  // Safe row class resolver to avoid undefined access in getRowClassName
  const rowClassName = (rowIndex: number) => {
    if (!Array.isArray(tableData) || rowIndex < 0 || rowIndex >= tableData.length) return null;
    const row = tableData[rowIndex];
    if (!Array.isArray(row) || row.length === 0) return null;
    return ReportService.getRowClassName(row, [...HIERARCHY]);
  };

  const exportToExcel = () => {
    try {
      const header = columns.map(c => c);
      const ws = XLSX.utils.aoa_to_sheet([header, ...tableData]);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'WholeEarth');
      const fileName = `WholeEarth_${EpochMonthService.epochMonthToMMMYY(epochMonth ?? EpochMonthService.getTodayEpochMonth())}.xlsx`;
      XLSX.writeFile(wb, fileName);
    } catch (e) {
      console.error(e);
    }
  };

  const exportToCsv = () => {
    try {
      const header = columns.map(c => c);
      const rows = [header, ...tableData];
      const csv = rows.map(r => r.map(v => {
        const s = String(v ?? "");
        if (s.includes(',') || s.includes('"') || s.includes('\n')) {
          return '"' + s.replace(/"/g, '""') + '"';
        }
        return s;
      }).join(',')).join('\n');
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `WholeEarth_${EpochMonthService.epochMonthToMMMYY(epochMonth ?? EpochMonthService.getTodayEpochMonth())}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (e) {
      console.error(e);
    }
  };

  // Add PDF export using CustomReportService wide-table helper
  const exportToPdf = async () => {
    try {
      const fileName = `WholeEarth_${EpochMonthService.epochMonthToMMMYY(epochMonth ?? EpochMonthService.getTodayEpochMonth())}`;
      const title = `${EpochMonthService.epochMonthToMMMYY(epochMonth ?? EpochMonthService.getTodayEpochMonth())} Whole Earth Custom`;
      const subtitle = 'Category by Location';
      await CustomReportService.tableToPdfWide({
        columns,
        data: tableData,
        fileName,
        title,
        subtitle,
        orientation: 'l',
        pageWidthPt: 1700,
        pageHeightPt: 1100,
        fontSizePt: 7,
        minColWidthPt: 40,
        maxColWidthPt: 240,
      });
    } catch (e) {
      console.error(e);
    }
  };

  return (
    <div className="w-full h-full flex flex-col items-center justify-center text-center max-w-[1700px]">
      <div className="w-full flex items-center gap-4 px-4 py-3">
        <div className="flex items-center gap-2">
          <label className="text-sm">Location:</label>
          <MultiSelect
            options={locations.map(l => ({ code: l.code, label: l.label }))}
            value={locationFilter}
            onChange={(v) => {
              try {
                const arr = Array.isArray(v) ? v : [];
                const codes = arr.map((item: any) => typeof item === 'string' ? item : (item?.code ?? '')).filter((c: string) => c && typeof c === 'string');
                setLocationFilter(codes.length ? codes : ["ALL"]);
              } catch {
                setLocationFilter(["ALL"]);
              }
            }}
            placeholder="All Locations"
          />
        </div>
        <div className="flex items-center gap-2">
          <label className="text-sm">Category:</label>
          <MultiSelect
            options={categories.map(c => ({ code: c.code, label: c.label }))}
            value={categoryFilter}
            onChange={(v) => {
              try {
                const arr = Array.isArray(v) ? v : [];
                const codes = arr.map((item: any) => typeof item === 'string' ? item : (item?.code ?? '')).filter((c: string) => c && typeof c === 'string');
                setCategoryFilter(codes.length ? codes : ["ALL"]);
              } catch {
                setCategoryFilter(["ALL"]);
              }
            }}
            placeholder="All Categories"
          />
        </div>
        <div className="flex-1" />
        <div className="flex items-center gap-2">
          <button
            onClick={exportToExcel}
            className="h-9 px-4 rounded-md text-[13px] font-semibold text-white bg-tw-primary hover:bg-sky-600 shadow transition"
          >
            Download Excel
          </button>
          <button
            onClick={exportToCsv}
            className="h-9 px-4 rounded-md text-[13px] font-semibold text-white bg-green-600 hover:bg-green-700 shadow transition"
          >
            Download CSV
          </button>
          <button
            onClick={exportToPdf}
            className="h-9 px-4 rounded-md text-[13px] font-semibold text-white bg-red-600 hover:bg-red-700 shadow transition"
          >
            Download PDF
          </button>
        </div>
      </div>
      {loading ? (
        <p>Loading...</p>
      ) : (
        <div className="w-full h-full">
          <ReportTable
            columns={columns.map((c) => ({ name: c, sortable: false }))}
            data={tableData}
            rowClassName={rowClassName}
            headerClassName="bg-slate-700 !text-white !text-center py-2 px-4"
            stickyHeader={true}
            hierarchyDepth={HIERARCHY.length}
          />
        </div>
      )}
    </div>
  );
};

export default WholeEarthCustomReport;
